/* eslint-disable react/no-danger */
import type { FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { siteDomain } from '@trello/config';

import { applyBidiTooltip } from './applyBidiTooltip';
import { TrelloMarkdownWrapper } from './TrelloMarkdownWrapper';

import styles from './TrelloMarkdown.less';

// eslint-disable-next-line @trello/no-module-logic
export const markdown = new TrelloMarkdownWrapper();

type MarkdownContentType = 'comment' | 'description' | 'checkitems' | 'bio';

interface AllProps {
  text: string;
  contentType: MarkdownContentType;
  options?: { textData?: object };
  className?: string;
}

export const TrelloMarkdown: FunctionComponent<AllProps> = ({
  text,
  contentType,
  options,
  className,
}) => {
  let formattedContent: string;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      applyBidiTooltip(ref.current);
    }
  }, []);

  switch (contentType) {
    case 'comment':
      formattedContent = markdown.comments.format(text, {
        siteDomain: siteDomain,
        ...options,
      }).output;
      break;
    case 'description':
      formattedContent = markdown.description.format(text, options).output;
      break;
    case 'checkitems':
      formattedContent = markdown.checkItems.format(text, options).output;
      break;
    case 'bio':
      formattedContent = markdown.bio.format(text, options).output;
      break;
    default:
      throw new Error('Invalid MarkdownContentType for MarkdownContainer.');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(formattedContent, 'text/html');

  markdown.addLineNumbers(doc.body);

  return (
    <div
      className={classNames(styles.markdown, className)}
      ref={ref}
      dangerouslySetInnerHTML={{
        __html: doc.body.innerHTML,
      }}
    />
  );
};
